import React, { useEffect, useState } from 'react';
import api from '../../../../services/api';
import { useTopologyBase } from './topologyApi';
import {
  Drawer, Box, Typography, Divider, Chip, IconButton, Stack, List, ListItem, ListItemText, Alert,
  CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Table, TableBody, TableCell, TableRow, Tooltip,
  Tabs, Tab, TextField, InputAdornment, Popover, Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HubIcon from '@mui/icons-material/Hub';
import PolylineIcon from '@mui/icons-material/Polyline';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import RouterIcon from '@mui/icons-material/Router';
import LanIcon from '@mui/icons-material/Lan';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const BACKUP_LABEL = {
  ok:      { label: 'Backup recente',           color: 'success', severity: 'success' },
  old:     { label: 'Backup antigo (>48h)',     color: 'warning', severity: 'warning' },
  stale:   { label: 'Backup muito antigo (>7d)', color: 'error',   severity: 'error' },
  missing: { label: 'Sem backup',               color: 'error',   severity: 'error' },
  unknown: { label: '?',                        color: 'default', severity: 'info' },
};

function KV({ k, v }) {
  if (v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) return null;
  return (
    <TableRow>
      <TableCell sx={{ pl: 0, py: 0.3, border: 0, color: 'text.secondary', width: 90 }}>
        <Typography variant="caption">{k}</Typography>
      </TableCell>
      <TableCell sx={{ py: 0.3, border: 0, wordBreak: 'break-word' }}>
        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
          {Array.isArray(v) ? v.join(', ') : String(v)}
        </Typography>
      </TableCell>
    </TableRow>
  );
}

function PolicyFlag({ label, tone = 'default' }) {
  const colors = {
    default: { bg: '#334155', fg: '#e2e8f0' },
    in:      { bg: '#1e40af', fg: '#dbeafe' },
    out:     { bg: '#166534', fg: '#dcfce7' },
    warn:    { bg: '#b45309', fg: '#fef3c7' },
    danger:  { bg: '#991b1b', fg: '#fee2e2' },
  };
  const c = colors[tone] || colors.default;
  return (
    <Chip
      label={label}
      size="small"
      sx={{ height: 18, fontSize: 10, bgcolor: c.bg, color: c.fg, fontWeight: 600 }}
    />
  );
}

// Context singleton passando route_policies pro PolicyLink resolver
// o conteúdo sem precisar prop-drilling pelo BgpPeerCard.
const PolicyContext = React.createContext({ route_maps: [], prefix_lists: [], community_lists: [], as_path_lists: [] });

/**
 * Link clicável com nome de policy (rm:XXX, pl:XXX, etc). Ao clicar abre
 * popover com o CONTEÚDO da policy — entries permit/deny, prefixes, sets.
 * Kind: 'route_map' | 'prefix_list' | 'community_list' | 'as_path_list'
 */
function PolicyLink({ kind, name, prefix }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const policies = React.useContext(PolicyContext);

  const collections = {
    route_map: policies.route_maps || [],
    prefix_list: policies.prefix_lists || [],
    community_list: policies.community_lists || [],
    as_path_list: policies.as_path_lists || [],
  };
  const found = (collections[kind] || []).find((p) => p.name === name);

  const hasContent = found && (found.entries || []).length > 0;
  const open = Boolean(anchorEl);

  return (
    <>
      <Link
        component="button"
        type="button"
        onClick={(e) => { e.stopPropagation(); if (hasContent) setAnchorEl(e.currentTarget); }}
        sx={{
          fontFamily: 'monospace', fontSize: 11, p: 0, verticalAlign: 'baseline',
          color: hasContent ? '#60a5fa' : 'text.disabled',
          cursor: hasContent ? 'pointer' : 'default',
          textDecoration: hasContent ? 'underline dotted' : 'none',
        }}
      >
        {prefix}{name}
      </Link>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1.5, maxWidth: 420, bgcolor: '#0f172a', border: '1px solid #334155' } } }}
      >
        <Typography variant="caption" sx={{ color: '#f1f5f9', fontWeight: 700, display: 'block', mb: 0.5 }}>
          {kind.replace('_', '-')}: {name}
        </Typography>
        {found && (found.entries || []).map((entry, idx) => {
          if (kind === 'route_map') {
            return (
              <Box key={idx} sx={{ mb: 0.5, pl: 0.5, borderLeft: `2px solid ${entry.action === 'permit' ? '#10b981' : '#ef4444'}` }}>
                <Typography variant="caption" sx={{ color: '#cbd5e1', fontFamily: 'monospace', display: 'block' }}>
                  seq {entry.seq} {entry.action}
                </Typography>
                {(entry.match || []).map((m, i) => (
                  <Typography key={`m${i}`} variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace', display: 'block', pl: 1 }}>
                    match {m.kind} {m.value}
                  </Typography>
                ))}
                {(entry.set || []).map((s, i) => (
                  <Typography key={`s${i}`} variant="caption" sx={{ color: '#fbbf24', fontFamily: 'monospace', display: 'block', pl: 1 }}>
                    set {s.kind} {s.value}
                  </Typography>
                ))}
              </Box>
            );
          }
          if (kind === 'prefix_list') {
            const lege = [
              entry.ge != null ? `ge ${entry.ge}` : null,
              entry.le != null ? `le ${entry.le}` : null,
            ].filter(Boolean).join(' ');
            return (
              <Typography key={idx} variant="caption" sx={{
                color: entry.action === 'permit' ? '#86efac' : '#fca5a5',
                fontFamily: 'monospace', display: 'block',
              }}>
                {entry.seq != null ? `${entry.seq} ` : ''}{entry.action} {entry.prefix}{lege ? ' ' + lege : ''}
              </Typography>
            );
          }
          // community_list / as_path_list
          return (
            <Typography key={idx} variant="caption" sx={{
              color: entry.action === 'permit' ? '#86efac' : '#fca5a5',
              fontFamily: 'monospace', display: 'block',
            }}>
              {entry.action} {entry.value || entry.regex}
            </Typography>
          );
        })}
        {!hasContent && (
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            (conteúdo não capturado — parser não encontrou definição)
          </Typography>
        )}
      </Popover>
    </>
  );
}

function IfaceBadge({ name }) {
  if (!name) return null;
  return (
    <Tooltip title={`via interface ${name}`}>
      <Chip
        label={name}
        size="small"
        variant="outlined"
        sx={{
          height: 18,
          fontSize: 10,
          fontFamily: 'monospace',
          borderColor: '#60a5fa',
          color: '#60a5fa',
        }}
      />
    </Tooltip>
  );
}

function BgpPeerCard({ peer }) {
  return (
    <Box sx={{ mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
          {peer.ip}
        </Typography>
        {peer.remote_as && (
          <Chip label={`AS${peer.remote_as}`} size="small" sx={{ height: 18, fontSize: 10 }} />
        )}
        <PolicyFlag label={peer.family || 'ipv4'} tone={peer.family === 'ipv6' ? 'in' : 'default'} />
        {/* Interface em destaque — source of truth pra saber onde a sessão sai.
            Quando connect_interface é SVI (Vlanif170), mostra também o trunk físico
            resolvido via trunks_by_vlan (ex: Te1/1/1) pra operador saber a porta real. */}
        <IfaceBadge name={peer.connect_interface} />
        {peer.connect_physical_iface && peer.connect_physical_iface !== peer.connect_interface && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>via</Typography>
            <Tooltip title={`porta física resolvida: ${peer.connect_physical_iface}`}>
              <Chip
                label={peer.connect_physical_iface}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontFamily: 'monospace',
                  bgcolor: '#1e3a8a',
                  color: '#bfdbfe',
                  fontWeight: 700,
                }}
              />
            </Tooltip>
          </>
        )}
        {peer.peer_group && <Chip label={peer.peer_group} size="small" variant="outlined" sx={{ height: 18, fontSize: 10 }} />}
        {peer.route_reflector_client && <PolicyFlag label="RR" tone="warn" />}
        {peer.enabled === false && <PolicyFlag label="DISABLED" tone="danger" />}
        {peer.bfd_enabled && <PolicyFlag label="BFD" tone="in" />}
        {peer.next_hop_local && <PolicyFlag label="next-hop-self" />}
        {peer.remove_private_as && <PolicyFlag label="rm private-as" />}
        {peer.default_originate && <PolicyFlag label="default-orig" />}
      </Box>
      {peer.description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
          "{peer.description}"
        </Typography>
      )}
      <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
        {(peer.route_map_in || peer.prefix_list_in || peer.as_path_filter_in) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <PolicyFlag label="IN ←" tone="in" />
            {peer.route_map_in && <PolicyLink kind="route_map" name={peer.route_map_in} prefix="rm:" />}
            {peer.prefix_list_in && <PolicyLink kind="prefix_list" name={peer.prefix_list_in} prefix="pl:" />}
            {peer.as_path_filter_in && <PolicyLink kind="as_path_list" name={peer.as_path_filter_in} prefix="as-path:" />}
          </Box>
        )}
        {(peer.route_map_out || peer.prefix_list_out || peer.as_path_filter_out) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <PolicyFlag label="OUT →" tone="out" />
            {peer.route_map_out && <PolicyLink kind="route_map" name={peer.route_map_out} prefix="rm:" />}
            {peer.prefix_list_out && <PolicyLink kind="prefix_list" name={peer.prefix_list_out} prefix="pl:" />}
            {peer.as_path_filter_out && <PolicyLink kind="as_path_list" name={peer.as_path_filter_out} prefix="as-path:" />}
          </Box>
        )}
        {peer.max_prefixes && (
          <Typography variant="caption" color="text.secondary">
            max-prefix: {peer.max_prefixes.limit} ({peer.max_prefixes.action})
          </Typography>
        )}
        {peer.ebgp_multihop && (
          <Typography variant="caption" color="text.secondary">
            ebgp-multihop: {peer.ebgp_multihop}
          </Typography>
        )}
        {peer.timers && (
          <Typography variant="caption" color="text.secondary">
            timers: keepalive {peer.timers.keepalive}s · hold {peer.timers.hold}s
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function Section({ icon, title, count, children, defaultExpanded = false }) {
  return (
    <Accordion
      disableGutters
      defaultExpanded={defaultExpanded}
      sx={{ boxShadow: 'none', '&:before': { display: 'none' }, bgcolor: 'transparent' }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          {icon}
          <Typography variant="overline" color="text.secondary">{title}</Typography>
          {count != null && (
            <Chip label={count} size="small" sx={{ ml: 'auto', height: 18, fontSize: 11 }} />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0, pt: 0, pb: 1 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

export default function HostDrawer({ open, onClose, node, graph }) {
  const base = useTopologyBase();
  // O payload do mapa deixou de carregar interfaces completas, peers BGP,
  // route-policies, VLANs detalhadas, MPLS, OSPF e LAG de TODOS os nós — eram
  // 3,4 MB dos 4,7 MB, mandados em toda carga de mapa pra talvez nunca serem
  // abertos. Agora vêm daqui, quando o drawer abre de fato.
  const [detalhe, setDetalhe] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erroDet, setErroDet] = useState(null);

  useEffect(() => {
    let vivo = true;
    if (!open || !node?.id || !base) { setDetalhe(null); return undefined; }
    setCarregando(true);
    setErroDet(null);
    api.get(`${base}/host/${node.id}`)
      .then((res) => vivo && setDetalhe(res.data))
      .catch((e) => {
        if (!vivo) return;
        setDetalhe(null);
        setErroDet(e?.response?.data?.detail || e?.message || 'falha ao carregar detalhe');
      })
      .finally(() => vivo && setCarregando(false));
    return () => { vivo = false; };
  }, [open, node?.id, base]);

  if (!node || !graph) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box sx={{ width: 420, p: 2 }}>
          <Typography>Nenhum host selecionado.</Typography>
        </Box>
      </Drawer>
    );
  }

  const relatedEdges = (graph.edges || []).filter(
    (e) => String(e.source) === String(node.id) || String(e.target) === String(node.id),
  );
  // Os achados deste equipamento vêm no detalhe — graph.issues não existe
  // mais no payload do mapa.
  const relatedIssues = detalhe?.issues || [];

  const bkp = BACKUP_LABEL[node.backup_status] || BACKUP_LABEL.unknown;

  // Schema v2 fields — podem não existir em empresas antes do rebuild
  const bgpPeers = detalhe?.bgp_peers || [];
  const bgpNetworks = detalhe?.bgp_networks || [];
  const bgpNetworksV6 = detalhe?.bgp_networks_v6 || [];
  const bgpRedist = detalhe?.bgp_redistribute || [];
  const ospfGlobal = detalhe?.ospf_global;
  const ospfAreas = detalhe?.ospf_areas || [];
  const mplsGlobal = detalhe?.mpls_global;
  const mplsL3vpns = detalhe?.mpls_l3vpns || [];
  const mplsL2vpns = detalhe?.mpls_l2vpns || [];
  const vlansDetailed = detalhe?.vlans_detailed || [];
  const erpsRings = node.erps_rings || [];
  const isisGlobal = detalhe?.isis_global;
  const interfaces = detalhe?.interfaces || [];
  const lagBundles = detalhe?.lag_bundles || {};

  // Index ifaces por área OSPF (area_name -> [iface_name, ...])
  const ifacesByOspfArea = {};
  interfaces.forEach((i) => {
    if (i.ospf_area) {
      (ifacesByOspfArea[i.ospf_area] = ifacesByOspfArea[i.ospf_area] || []).push(i);
    }
  });
  // Index ifaces por VRF
  const ifacesByVrf = {};
  interfaces.forEach((i) => {
    if (i.vrf) {
      (ifacesByVrf[i.vrf] = ifacesByVrf[i.vrf] || []).push(i);
    }
  });

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 460, p: 2, overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>
            {node.label}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
          {node.vendor && <Chip label={node.vendor} size="small" />}
          {node.role && <Chip label={node.role} size="small" color="primary" />}
          {node.asn && <Chip label={`AS${node.asn}`} size="small" color="secondary" />}
          {node.bgp_role && node.bgp_role !== 'none' && (
            <Chip label={node.bgp_role} size="small" variant="outlined" />
          )}
          {node.ospf_role && node.ospf_role !== 'none' && (
            <Chip label={`ospf ${node.ospf_role}`} size="small" variant="outlined" />
          )}
          {node.issues_count > 0 && (
            <Chip
              label={`${node.issues_count} issue${node.issues_count > 1 ? 's' : ''}`}
              size="small"
              color="error"
            />
          )}
        </Stack>

        <Alert severity={bkp.severity} sx={{ mt: 2 }}>
          {bkp.label}
          {node.backup_age_hours != null && ` · ${Math.round(node.backup_age_hours)}h atrás`}
        </Alert>

        {carregando && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">
              carregando detalhe do equipamento…
            </Typography>
          </Box>
        )}
        {erroDet && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Não foi possível carregar o detalhe: {String(erroDet)}
          </Alert>
        )}

        {/* Identificação */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="overline" color="text.secondary">Identificação</Typography>
        <Table size="small">
          <TableBody>
            <KV k="ID" v={node.id} />
            <KV k="Sysname" v={node.sysname} />
            <KV k="Mgmt IP" v={node.mgmt_ip} />
            <KV k="Router ID" v={node.router_id} />
            <KV k="Zabbix hostid" v={node.hostid_zabbix} />
          </TableBody>
        </Table>

        {/* BGP — envelopado em PolicyContext pra PolicyLink resolver conteúdo
            das route-maps/prefix-lists/community-lists/as-path-filters */}
        {(bgpPeers.length > 0 || bgpNetworks.length > 0) && (
          <PolicyContext.Provider value={detalhe?.route_policies || {}}>
          <Section
            icon={<RouterIcon fontSize="small" sx={{ color: '#e67e22' }} />}
            title="BGP"
            count={bgpPeers.length}
          >
            {/* Bloco "IP prefix anunciados" — todas as networks + redistributes numa seção só */}
            {(bgpNetworks.length > 0 || bgpNetworksV6.length > 0 || bgpRedist.length > 0) && (
              <Box sx={{ mb: 1, p: 1, bgcolor: 'rgba(230,126,34,0.08)', border: '1px solid rgba(230,126,34,0.3)', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#e67e22', fontWeight: 700, display: 'block', mb: 0.5 }}>
                  IP prefixes anunciados
                </Typography>
                {bgpNetworks.length > 0 && (
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      IPv4 networks ({bgpNetworks.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                      {bgpNetworks.map((n) => (
                        <Chip
                          key={n}
                          label={n}
                          size="small"
                          sx={{ height: 18, fontSize: 10, fontFamily: 'monospace',
                            bgcolor: 'rgba(230,126,34,0.2)', color: '#e67e22' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                {bgpNetworksV6.length > 0 && (
                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      IPv6 networks ({bgpNetworksV6.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                      {bgpNetworksV6.map((n) => (
                        <Chip
                          key={n}
                          label={n}
                          size="small"
                          sx={{ height: 18, fontSize: 10, fontFamily: 'monospace',
                            bgcolor: 'rgba(167,139,250,0.2)', color: '#a78bfa' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
                {bgpRedist.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Redistribute ({bgpRedist.length}):
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3, mt: 0.3 }}>
                      {bgpRedist.map((r, i) => (
                        <Chip
                          key={i}
                          label={`${r.from}${r.route_map ? ' ▸ rm:' + r.route_map : ''}${r.metric != null ? ' (m:' + r.metric + ')' : ''}`}
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: 10, fontFamily: 'monospace',
                            borderColor: '#e67e22', color: '#e67e22' }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Peers BGP — cada peer é uma card (BgpPeerCard) com policies */}
            {bgpPeers.map((p) => <BgpPeerCard key={p.ip} peer={p} />)}
          </Section>
          </PolicyContext.Provider>
        )}

        {/* OSPF */}
        {(ospfGlobal || ospfAreas.length > 0) && (
          <Section
            icon={<PolylineIcon fontSize="small" sx={{ color: '#9b59b6' }} />}
            title="OSPF"
            count={ospfAreas.length}
          >
            {ospfGlobal && (
              <Table size="small">
                <TableBody>
                  <KV k="Process" v={ospfGlobal.process_id} />
                  <KV k="Router-ID" v={ospfGlobal.router_id} />
                  <KV k="Ref BW" v={ospfGlobal.reference_bw} />
                  <KV k="Passive" v={ospfGlobal.passive_interfaces} />
                  <KV k="Redistribute" v={ospfGlobal.redistribute?.map((r) => r.from).join(', ')} />
                  {ospfGlobal.default_information_originate && <KV k="Default" v="originate" />}
                </TableBody>
              </Table>
            )}
            {ospfAreas.map((a, i) => {
              const areaIfaces = ifacesByOspfArea[a.area] || [];
              return (
                <Box key={i} sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip label={`area ${a.area}`} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
                    {a.type && a.type !== 'normal' && (
                      <PolicyFlag label={a.type} tone="warn" />
                    )}
                    {a.authentication && <PolicyFlag label={`auth:${a.authentication}`} tone="in" />}
                    {areaIfaces.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                        {areaIfaces.length} iface{areaIfaces.length > 1 ? 's' : ''}
                      </Typography>
                    )}
                  </Box>
                  {a.networks?.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
                      {a.networks.slice(0, 8).join(' · ')}{a.networks.length > 8 ? ' …' : ''}
                    </Typography>
                  )}
                  {areaIfaces.length > 0 && (
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                      {areaIfaces.map((iface) => {
                        const tip = [
                          iface.ips?.[0]?.cidr || iface.ips?.[0]?.addr,
                          iface.ospf_cost != null ? `cost ${iface.ospf_cost}` : null,
                          iface.ospf_network_type,
                          iface.ospf_hello_interval ? `hello ${iface.ospf_hello_interval}` : null,
                          iface.ospf_bfd ? 'BFD' : null,
                          iface.ospf_authentication ? `auth:${iface.ospf_authentication}` : null,
                        ].filter(Boolean).join(' · ');
                        return (
                          <Tooltip key={iface.name} title={tip || iface.name}>
                            <Chip
                              label={iface.name}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 18,
                                fontSize: 10,
                                fontFamily: 'monospace',
                                borderColor: '#a78bfa',
                                color: '#a78bfa',
                              }}
                            />
                          </Tooltip>
                        );
                      })}
                    </Box>
                  )}
                  {a.virtual_links?.length > 0 && (
                    <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.5 }}>
                      virtual-links: {a.virtual_links.map((v) => v.peer_router_id).join(', ')}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Section>
        )}

        {/* MPLS */}
        {(mplsGlobal || mplsL3vpns.length > 0 || mplsL2vpns.length > 0) && (
          <Section
            icon={<LanIcon fontSize="small" sx={{ color: '#2ecc71' }} />}
            title="MPLS"
            count={mplsL3vpns.length + mplsL2vpns.length}
          >
            {mplsGlobal && (
              <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                {mplsGlobal.ldp_enabled && <PolicyFlag label="LDP" tone="in" />}
                {mplsGlobal.te_enabled && <PolicyFlag label="TE" tone="warn" />}
                {mplsGlobal.sr_enabled && <PolicyFlag label="SR" tone="in" />}
                {mplsGlobal.ldp_router_id && (
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    lsr-id: {mplsGlobal.ldp_router_id}
                  </Typography>
                )}
              </Stack>
            )}
            {mplsL3vpns.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary">L3VPN (VRFs):</Typography>
                {mplsL3vpns.map((v, i) => {
                  const vrfIfaces = ifacesByVrf[v.vrf] || [];
                  return (
                    <Box key={i} sx={{ mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                        {v.vrf}
                      </Typography>
                      <Table size="small">
                        <TableBody>
                          <KV k="RD" v={v.rd} />
                          <KV k="RT import" v={v.rt_import} />
                          <KV k="RT export" v={v.rt_export} />
                        </TableBody>
                      </Table>
                      {vrfIfaces.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Interfaces atachadas ({vrfIfaces.length}):
                          </Typography>
                          <Box sx={{ mt: 0.3, display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                            {vrfIfaces.map((iface) => {
                              const firstIp = iface.ips?.[0]?.cidr || iface.ips?.[0]?.addr;
                              return (
                                <Tooltip key={iface.name} title={firstIp || iface.name}>
                                  <Chip
                                    label={iface.name}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 18,
                                      fontSize: 10,
                                      fontFamily: 'monospace',
                                      borderColor: '#2ecc71',
                                      color: '#2ecc71',
                                    }}
                                  />
                                </Tooltip>
                              );
                            })}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </>
            )}
            {mplsL2vpns.length > 0 && (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>L2VPN:</Typography>
                {mplsL2vpns.map((v, i) => (
                  <Box key={i} sx={{ mt: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                      {v.vsi}
                      {v.vsi_id != null && ` (id=${v.vsi_id})`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      peers: {(v.peers || []).join(', ') || '—'}
                      {v.signaling && ` · ${v.signaling}`}
                      {v.encap && ` · ${v.encap}`}
                    </Typography>
                  </Box>
                ))}
              </>
            )}
          </Section>
        )}

        {/* VLANs detalhadas — cada VLAN mostra quais interfaces a carregam
            com marcador T (tagged/trunk) ou U (untagged/access). */}
        {vlansDetailed.length > 0 && (
          <Section
            icon={<LanIcon fontSize="small" sx={{ color: '#16a085' }} />}
            title="VLANs"
            count={vlansDetailed.length}
          >
            {vlansDetailed.slice(0, 60).map((v) => {
              // Resolve interfaces que carregam essa VLAN
              const tagged = []; // trunk members (vlan_allowed inclui vid)
              const untagged = []; // access port (pvid === vid)
              const subIface = []; // subinterface dot1q_tag === vid
              interfaces.forEach((i) => {
                const va = i.vlan_allowed || [];
                const hasAllowed = va.includes(v.vid);
                const isPvid = i.pvid === v.vid;
                const isDot1q = i.dot1q_tag === v.vid;
                if (isDot1q) subIface.push(i);
                else if (hasAllowed) tagged.push(i);
                else if (isPvid) untagged.push(i);
              });

              const hasAny = tagged.length + untagged.length + subIface.length > 0;

              return (
                <Box key={v.vid} sx={{ mb: 0.8, p: 0.8, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                      label={v.vid}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        minWidth: 38,
                        fontWeight: 700,
                        bgcolor: '#16a085',
                        color: '#fff',
                      }}
                    />
                    {v.name && (
                      <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {v.name}
                      </Typography>
                    )}
                    {v.is_l3svi && <PolicyFlag label="SVI" tone="in" />}
                    {v.is_management && <PolicyFlag label="mgmt" tone="warn" />}
                    {v.is_voice && <PolicyFlag label="voice" />}
                    {v.svi_ip && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', ml: 'auto' }}>
                        {v.svi_ip}
                      </Typography>
                    )}
                  </Box>

                  {/* Interfaces que carregam a VLAN */}
                  {hasAny && (
                    <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                      {tagged.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.3, flexWrap: 'wrap' }}>
                          <Chip label="T" size="small" sx={{ height: 14, fontSize: 9, bgcolor: '#16a085', color: '#fff', fontWeight: 700, minWidth: 18 }} />
                          {tagged.map((i) => (
                            <Tooltip key={i.name} title="trunk (tagged)">
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 10, color: '#0f766e' }}>
                                {i.name}
                              </Typography>
                            </Tooltip>
                          ))}
                        </Box>
                      )}
                      {untagged.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.3, flexWrap: 'wrap' }}>
                          <Chip label="U" size="small" sx={{ height: 14, fontSize: 9, bgcolor: '#60a5fa', color: '#fff', fontWeight: 700, minWidth: 18 }} />
                          {untagged.map((i) => (
                            <Tooltip key={i.name} title="access port (untagged / pvid)">
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 10, color: '#1e40af' }}>
                                {i.name}
                              </Typography>
                            </Tooltip>
                          ))}
                        </Box>
                      )}
                      {subIface.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.3, flexWrap: 'wrap' }}>
                          <Chip label=".Q" size="small" sx={{ height: 14, fontSize: 9, bgcolor: '#a78bfa', color: '#fff', fontWeight: 700, minWidth: 18 }} />
                          {subIface.map((i) => (
                            <Tooltip key={i.name} title="subinterface dot1q">
                              <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: 10, color: '#5b21b6' }}>
                                {i.name}
                              </Typography>
                            </Tooltip>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
            {vlansDetailed.length > 60 && (
              <Typography variant="caption" color="text.secondary">
                … +{vlansDetailed.length - 60} outras
              </Typography>
            )}
          </Section>
        )}

        {/* ERPS rings */}
        {erpsRings.length > 0 && (
          <Section
            icon={<DonutLargeIcon fontSize="small" sx={{ color: '#06b6d4' }} />}
            title="ERPS rings"
            count={erpsRings.length}
          >
            {erpsRings.map((r, i) => (
              <Box key={i} sx={{ mb: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip label={`Ring ${r.ring_id}`} size="small" sx={{ bgcolor: '#06b6d4', color: '#000', height: 18 }} />
                  {r.role === 'rpl_owner' && <PolicyFlag label="RPL OWNER" tone="warn" />}
                  {r.role === 'rpl_neighbor' && <PolicyFlag label="RPL NEIGHBOR" tone="in" />}
                  {r.version && <Typography variant="caption">v{r.version}</Typography>}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3, fontFamily: 'monospace' }}>
                  east: {r.east_port || '—'} · west: {r.west_port || '—'}
                </Typography>
                {r.control_vlan && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    RAPS VID {r.control_vlan} · data [{(r.data_vlans || []).slice(0, 10).join(',')}
                    {(r.data_vlans || []).length > 10 ? '…' : ''}]
                  </Typography>
                )}
              </Box>
            ))}
          </Section>
        )}

        {/* LAG bundles */}
        {Object.keys(lagBundles).length > 0 && (
          <Section
            icon={<HubIcon fontSize="small" sx={{ color: '#60a5fa' }} />}
            title="LAG bundles"
            count={Object.keys(lagBundles).length}
          >
            {Object.entries(lagBundles).map(([bname, info]) => (
              <Box key={bname} sx={{ mb: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip label={bname} size="small"
                    sx={{ bgcolor: '#1e3a8a', color: '#dbeafe', fontFamily: 'monospace', height: 20, fontWeight: 700 }} />
                  {info.mode && <PolicyFlag label={info.mode.toUpperCase()} tone={info.mode === 'lacp' ? 'in' : 'default'} />}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {(info.members || []).length} members
                  </Typography>
                </Box>
                <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                  {(info.members || []).map((m) => {
                    // Busca status do membro nas interfaces
                    const member = interfaces.find((i) => i.name === m);
                    const admin = member?.admin_down;
                    return (
                      <Box key={m} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: admin ? 'error.main' : 'text.primary' }}>
                          {admin ? '✕' : '●'} {m}
                        </Typography>
                        {member?.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                            — {member.description.slice(0, 40)}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Section>
        )}

        {/* IS-IS (Lote 2 — só mostra se tiver) */}
        {isisGlobal && (
          <Section
            icon={<RouterIcon fontSize="small" sx={{ color: '#a78bfa' }} />}
            title="IS-IS"
          >
            <Table size="small">
              <TableBody>
                <KV k="Process" v={isisGlobal.process_id} />
                <KV k="NET" v={isisGlobal.net} />
                <KV k="Level" v={isisGlobal.level} />
                <KV k="Redistribute" v={isisGlobal.redistribute?.map((r) => r.from).join(', ')} />
              </TableBody>
            </Table>
          </Section>
        )}

        {/* Adjacências — movido pro fim (info contextual, não crítica).
            Default colapsado. */}
        <Section
          icon={<HubIcon fontSize="small" color="primary" />}
          title="Adjacências"
          count={relatedEdges.length}
          defaultExpanded={false}
        >
          <List dense disablePadding>
            {relatedEdges.slice(0, 30).map((e) => {
              const other = String(e.source) === String(node.id) ? e.target : e.source;
              const otherNode = (graph.nodes || []).find((n) => String(n.id) === String(other));
              const otherLabel = otherNode?.label || other;
              return (
                <ListItem key={e.id} disableGutters sx={{ py: 0.3 }}>
                  <ListItemText
                    primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 600 } }}
                    secondaryTypographyProps={{ variant: 'caption', sx: { fontFamily: 'monospace' } }}
                    primary={otherLabel}
                    secondary={`${(e.kinds || []).join(', ')}${e.subnet ? ' · ' + e.subnet : ''}`}
                  />
                </ListItem>
              );
            })}
            {relatedEdges.length > 30 && (
              <ListItem disableGutters>
                <ListItemText secondary={`... +${relatedEdges.length - 30} outras`} />
              </ListItem>
            )}
          </List>
        </Section>

        {/* Issues (mantido) */}
        {relatedIssues.length > 0 && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography variant="overline" color="text.secondary">
              Issues ({relatedIssues.length})
            </Typography>
            <List dense disablePadding>
              {relatedIssues.map((i, idx) => (
                <ListItem key={idx} disableGutters>
                  <ListItemText
                    primary={<Chip label={i.kind} size="small" color="error" />}
                    secondary={i.detail}
                    secondaryTypographyProps={{ sx: { wordBreak: 'break-word' } }}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Box>
    </Drawer>
  );
}
